import i18n, { type BackendModule, type ReadCallback } from 'i18next';
import { initReactI18next } from 'react-i18next';

import tr from './locales/tr.json';
import { LANGUAGE_STORAGE_KEY, LanguageCode } from './i18n/languageConstants';

/**
 * Beetegre V2 — i18n yapılandırması.
 *
 * Desteklenen diller: tr (varsayılan), en
 * Dil tercihi localStorage'da saklanır.
 * Eksik anahtar olduğunda TR fallback kullanılır.
 *
 * Only the fallback bundle ships in the initial load. Every other language is fetched on
 * demand: both bundles together are ~40% of the first paint, and the fallback is needed by
 * every user anyway, so bundling the second one made every Turkish user download English.
 */
const onDemandLocales: Partial<Record<string, () => Promise<{ default: object }>>> = {
  [LanguageCode.En]: () => import('./locales/en.json'),
};

const onDemandBackend: BackendModule = {
  type: 'backend',
  init() {},
  read(language: string, _namespace: string, callback: ReadCallback) {
    const load = onDemandLocales[language];
    if (!load) {
      callback(null, {});
      return;
    }
    load().then(
      (module) => callback(null, module.default),
      (error: unknown) => callback(error instanceof Error ? error : new Error(String(error)), false),
    );
  },
};

/** Resolves once the stored language's bundle is loaded; the app must not render before it. */
export const i18nReady: Promise<unknown> = i18n
  .use(initReactI18next)
  .use(onDemandBackend)
  .init({
    resources: {
      tr: { translation: tr },
    },
    partialBundledLanguages: true,
    lng: localStorage.getItem(LANGUAGE_STORAGE_KEY) ?? LanguageCode.Tr,
    fallbackLng: LanguageCode.Tr,
    interpolation: {
      escapeValue: false,
    },
    // A key absent from both bundles renders on screen as its own dotted identifier, and
    // nothing reports it — the backend emits keys as error messages, so one that was never
    // translated only surfaces when a customer sends a screenshot. `saveMissing` turns that
    // into a console error during development.
    //
    // Deliberately NOT `parseMissingKeyHandler`: that one changes what `t()` RETURNS, and
    // several call sites detect a missing key with the `t(key) === key` idiom (the permission
    // label lookup in mapBackendErrors.ts and extractApiError.ts, translateApiMessage). A
    // handler that returned anything else would make those read a missing key as a real
    // translation. `missingKeyHandler` is a pure side effect and leaves the value alone.
    saveMissing: import.meta.env.DEV,
    missingKeyHandler: (lngs, _ns, key) => {
      console.error(`[i18n] Eksik anahtar: "${key}" (${lngs.join(', ')})`);
    },
  });

// Keep the document language attribute in sync with the active i18next language
// so assistive tech announces content in the right language. Fires on init and
// on every changeLanguage (switcher choice + post-login account-language sync).
const syncHtmlLang = (lng: string) => {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lng;
  }
};
syncHtmlLang(i18n.resolvedLanguage ?? i18n.language);
i18n.on('languageChanged', syncHtmlLang);

export default i18n;
